<template>
  <div class='min-h-screen px-4 py-8 md:px-10'>
    <h1 class='text-2xl text-gray-900'>Faturamento clientes</h1>
    <form class='mt-6 grid grid-cols-1 items-end gap-4 border-b border-gray-200 pb-6 sm:grid-cols-2 xl:grid-cols-5' @submit.prevent='gerar'>
      <label class='flex min-w-0 flex-col gap-1 text-sm text-gray-600'>
        A partir de
        <input v-model='de' type='date' required :disabled='ocupado' class='h-11 rounded-lg border border-gray-300 px-3' />
      </label>
      <label class='flex min-w-0 flex-col gap-1 text-sm text-gray-600'>
        Até
        <input v-model='ate' type='date' required :min='de' :disabled='ocupado' class='h-11 rounded-lg border border-gray-300 px-3' />
      </label>
      <label class='flex min-w-0 flex-col gap-1 text-sm text-gray-600'>
        Cliente
        <select v-model='clienteId' :disabled='ocupado || carregandoClientes' class='h-11 w-full rounded-lg border border-gray-300 bg-white px-3'>
          <option value=''>Todos</option>
          <option v-for='cliente in clientes' :key='cliente._id' :value='cliente._id'>{{ cliente.nome }} ({{ cliente.email }})</option>
        </select>
      </label>
      <button type='submit' :disabled='ocupado || carregandoClientes' class='h-11 rounded-lg px-4 text-sm font-medium text-white disabled:opacity-50' :style='{ backgroundColor: primaryColor }'>
        {{ carregando ? 'Gerando...' : 'Gerar relatório' }}
      </button>
      <button type='button' :disabled='ocupado || carregandoClientes' class='flex h-11 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 disabled:opacity-50' @click='baixar'>
        <hero-icon name='arrow-down' class='h-5 w-5' />
        {{ exportando ? 'Exportando...' : 'Exportar CSV' }}
      </button>
    </form>

    <p v-if='erro' role='alert' class='mt-4 text-sm text-red-700'>{{ erro }}</p>
    <div v-if='progressoId' role='status' class='mt-6 rounded-lg border border-gray-200 bg-white px-5 py-4 text-sm text-gray-700'>
      <div class='flex flex-wrap justify-between gap-2'>
        <span>Progressão: <strong class='text-green-700'>{{ progresso }}%</strong> / 100%</span>
        <span v-if='!ocupado && progresso === 100 && !erro' class='text-green-700'>Relatório gerado com sucesso.</span>
      </div>
      <progress :value='progresso' max='100' aria-label='Progressão do faturamento' class='mt-3 block h-2 w-full' />
    </div>
    <div class='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4'>
      <div v-for='resumo in resumos' :key='resumo.quantidade' class='min-w-0 rounded-lg border border-gray-200 bg-white p-5'>
        <p class='text-sm font-medium text-gray-500'>{{ resumo.titulo }}</p>
        <p class='mt-2 break-words text-2xl font-medium' :class='resumo.cor'>{{ quantidade(totais[resumo.quantidade]) }}</p>
        <p class='mt-1 break-words text-sm font-medium text-green-700'>{{ moeda(totais[resumo.valor]) }}</p>
      </div>
    </div>
    <template v-if='resultado && !carregando'>
      <div class='mt-6 flex flex-wrap items-baseline justify-between gap-3'>
        <p class='text-sm text-gray-600'>{{ resultado.data.length }} cliente(s) · {{ dataFormatada(resultado.de) }} a {{ dataFormatada(resultado.ate) }}</p>
        <p class='text-lg font-medium text-green-700'>Total: {{ moeda(resultado.totais.total) }}</p>
      </div>
      <div class='mt-4 overflow-x-auto'>
        <table class='w-full text-left text-sm'>
          <thead class='border-b border-gray-300 bg-gray-50 text-gray-600'>
            <tr>
              <th class='px-3 py-3'>Cliente</th>
              <th v-for='coluna in colunas' :key='coluna.valor' class='whitespace-nowrap px-3 py-3 text-right'>{{ coluna.titulo }}</th>
              <th class='px-3 py-3 text-right'>Personalização</th>
              <th class='px-3 py-3 text-right'>Total</th>
            </tr>
          </thead>
          <tbody class='divide-y divide-gray-200'>
            <tr v-for='linha in linhasPaginadas' :key='linha._id'>
              <td class='min-w-[200px] max-w-xs break-words px-3 py-4'>
                <span class='font-medium text-gray-900'>{{ linha.nome }}</span>
                <span class='block text-xs text-gray-500'>{{ linha.email }}</span>
              </td>
              <td v-for='coluna in colunas' :key='coluna.valor' class='whitespace-nowrap px-3 py-4 text-right'>
                {{ moeda(linha[coluna.valor]) }}
                <span class='block text-xs text-gray-500'>{{ quantidade(linha[coluna.quantidade]) }}</span>
              </td>
              <td class='whitespace-nowrap px-3 py-4 text-right'>{{ moeda(linha.valorPersonalizado) }}</td>
              <td class='whitespace-nowrap px-3 py-4 text-right font-medium text-green-700'>{{ moeda(linha.total) }}</td>
            </tr>
            <tr v-if='!resultado.data.length'><td colspan='7' class='py-8 text-center text-gray-500'>Nenhum cliente encontrado.</td></tr>
          </tbody>
          <tfoot v-if='resultado.data.length' class='border-t border-gray-300 bg-gray-50 font-medium'>
            <tr>
              <td class='px-3 py-4'>Total geral</td>
              <td v-for='coluna in colunas' :key='coluna.valor' class='whitespace-nowrap px-3 py-4 text-right'>
                {{ moeda(resultado.totais[coluna.valor]) }}
                <span class='block text-xs font-normal text-gray-500'>{{ quantidade(resultado.totais[coluna.quantidade]) }}</span>
              </td>
              <td class='whitespace-nowrap px-3 py-4 text-right'>{{ moeda(resultado.totais.valorPersonalizado) }}</td>
              <td class='whitespace-nowrap px-3 py-4 text-right text-green-700'>{{ moeda(resultado.totais.total) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div v-if='resultado.data.length' class='mt-4 flex flex-wrap items-center justify-between gap-4'>
        <div class='flex flex-wrap items-center gap-4 text-sm text-gray-600'>
          <span>{{ primeiroRegistro }}–{{ ultimoRegistro }} de {{ resultado.data.length }} clientes</span>
          <label class='flex items-center gap-2'>
            Por página
            <select v-model.number='porPagina' class='h-10 rounded-lg border border-gray-300 bg-white px-3' @change='pagina = 1'>
              <option v-for='tamanho in [10, 25, 50, 100]' :key='tamanho' :value='tamanho'>{{ tamanho }}</option>
            </select>
          </label>
        </div>
        <pagination v-if='resultado.data.length > porPagina' v-model='pagina' :per-page='porPagina' :records='resultado.data.length' :options='{ chunk: 3 }' class='pagination faturamento-paginacao' />
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import { colorMixin } from '@/utils/colorMixin';
import HeroIcon from 'vue-heroicons';
import { arrowDown } from 'vue-heroicons/src/icons';

HeroIcon.add([arrowDown]);

const endpoint = '/v1/campanhasLZV2/faturamento-clientes-bluesend';

export default {
  mixins: [colorMixin],
  data() {
    return {
      de: moment().startOf('month').format('YYYY-MM-DD'),
      ate: moment().endOf('month').format('YYYY-MM-DD'),
      clienteId: '',
      clientes: [],
      resultado: null,
      pagina: 1,
      porPagina: 25,
      carregando: false,
      carregandoClientes: true,
      exportando: false,
      progresso: 0,
      progressoId: null,
      eventoProgresso: '',
      erro: '',
      resumos: [
        { titulo: 'Envios', quantidade: 'totalMassa', valor: 'valorMassa', cor: 'text-green-700' },
        { titulo: 'Conversas ativas', quantidade: 'totalChats', valor: 'valorChats', cor: 'text-blue-700' },
        { titulo: 'Operadores ativos', quantidade: 'totalOperadores', valor: 'valorOperadores', cor: 'text-indigo-700' },
        { titulo: 'Validação WhatsApp', quantidade: 'totalValidacao', valor: 'valorValidacao', cor: 'text-yellow-600' }
      ],
      colunas: [
        { titulo: 'Envios', quantidade: 'totalMassa', valor: 'valorMassa' },
        { titulo: 'Conversas', quantidade: 'totalChats', valor: 'valorChats' },
        { titulo: 'Operadores', quantidade: 'totalOperadores', valor: 'valorOperadores' },
        { titulo: 'Validações', quantidade: 'totalValidacao', valor: 'valorValidacao' }
      ]
    };
  },
  computed: {
    linhasPaginadas() {
      if (!this.resultado) return [];
      const inicio = (this.pagina - 1) * this.porPagina;
      return this.resultado.data.slice(inicio, inicio + this.porPagina);
    },
    primeiroRegistro() {
      return this.resultado && this.resultado.data.length ? (this.pagina - 1) * this.porPagina + 1 : 0;
    },
    ultimoRegistro() {
      return this.resultado ? Math.min(this.pagina * this.porPagina, this.resultado.data.length) : 0;
    },
    totais() {
      return this.resultado ? this.resultado.totais : {};
    },
    ocupado() {
      return this.carregando || this.exportando;
    }
  },
  async mounted() {
    this.eventoProgresso = 'progressaoFaturamentoClientesBluesend_' + this.$store.state.user._id;
    this.sockets.subscribe(this.eventoProgresso, this.atualizarProgresso);
    try {
      const { data } = await this.$http.post(endpoint + '/clientes');
      if (!data.success) throw new Error(data.err);
      this.clientes = data.data;
    } catch (error) {
      this.exibirErro(error);
    } finally {
      this.carregandoClientes = false;
    }
  },
  beforeDestroy() {
    this.sockets.unsubscribe(this.eventoProgresso);
  },
  methods: {
    atualizarProgresso(evento) {
      if (!this.ocupado || !evento || evento.id !== this.progressoId) return;
      const valor = Number(evento.progresso);
      if (Number.isFinite(valor)) this.progresso = Math.max(this.progresso, Math.min(100, Math.max(0, valor)));
    },
    iniciarProgresso(filtros) {
      this.progresso = 0;
      this.progressoId = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
      filtros.progressoId = this.progressoId;
    },
    filtros() {
      if (!this.de || !this.ate || this.ate < this.de) {
        this.erro = 'Informe um período válido.';
        return null;
      }
      return { de: this.de, ate: this.ate, clienteId: this.clienteId || null };
    },
    async gerar() {
      const filtros = this.filtros();
      if (!filtros || this.ocupado) return;
      this.carregando = true;
      this.pagina = 1;
      this.erro = '';
      this.resultado = null;
      this.iniciarProgresso(filtros);
      try {
        const { data } = await this.$http.post(endpoint, filtros);
        if (!data.success) throw new Error(data.err);
        this.resultado = data;
        this.progresso = 100;
      } catch (error) {
        this.exibirErro(error);
      } finally {
        this.carregando = false;
      }
    },
    async baixar() {
      const filtros = this.filtros();
      if (!filtros || this.ocupado) return;
      this.exportando = true;
      this.erro = '';
      this.iniciarProgresso(filtros);
      try {
        const { data } = await this.$http.post(endpoint + '/csv', filtros, { responseType: 'blob' });
        if (data.type && data.type.indexOf('json') !== -1) {
          const resposta = JSON.parse(await data.text());
          throw new Error(resposta.err || 'Erro ao exportar relatório.');
        }
        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Faturamento-clientes-BlueSend-' + filtros.de + '-' + filtros.ate + '.csv';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        this.progresso = 100;
      } catch (error) {
        this.exibirErro(error);
      } finally {
        this.exportando = false;
      }
    },
    exibirErro(error) {
      this.erro = (error.response && error.response.data && error.response.data.err) ||
        error.message || 'Erro ao carregar faturamento.';
    },
    moeda(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    quantidade(valor) {
      return Number(valor || 0).toLocaleString('pt-BR');
    },
    dataFormatada(valor) {
      return moment(valor, 'YYYY-MM-DD').format('DD/MM/YYYY');
    }
  }
};
</script>

<style scoped>
.faturamento-paginacao {
  max-width: 100%;
  overflow-x: auto;
}

.faturamento-paginacao ::v-deep .VuePagination__pagination {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.faturamento-paginacao ::v-deep .VuePagination__pagination-item {
  flex: 0 0 auto;
}

.faturamento-paginacao ::v-deep a {
  display: flex;
  align-items: center;
  justify-content: center;
  float: none;
  min-width: 36px;
  height: 36px;
  margin: 0;
  padding: 0 10px;
}
</style>
